/**
 * Reviews Service
 * ===============
 * Manages book reviews after a borrow is returned.
 *
 * Security model:
 *   - Reviews written by Cloud Function (server-side) after verifying
 *     the request was actually returned
 *   - Rating average updated by Cloud Function (prevents manipulation)
 *   - Client can READ reviews freely
 *   - Client CANNOT write reviews directly (Firestore Rules block it)
 *
 * Client flow:
 *   1. Borrower taps "Rate this book" after markReturned()
 *   2. Client calls submitReview() → calls Cloud Function via HTTPS
 *   3. Cloud Function verifies request status, writes to Firestore
 *   4. Cloud Function updates book.rating and book.reviewCount
 */

import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { httpsCallable }  from "firebase/functions";
import { functions, db }  from "@/lib/firebase";
import type { Review, ApiResult } from "@/types";

// ─── TYPES ───────────────────────────────────────────────────────────────────
export interface SubmitReviewPayload {
  requestId:  string;
  bookId:     string;
  rating:     number;   // 1–5
  text?:      string;
}

// ─── SUBMIT REVIEW (via Cloud Function) ──────────────────────────────────────
/**
 * Calls the submitReview Cloud Function, which:
 *   1. Verifies the request is in "returned" status
 *   2. Verifies the caller is the borrower
 *   3. Writes the review to Firestore
 *   4. Updates book.rating and book.reviewCount atomically
 */
export async function submitReview(
  payload: SubmitReviewPayload
): Promise<ApiResult<void>> {
  try {
    const fn = httpsCallable<SubmitReviewPayload, { ok: boolean }>(
      functions,
      "submitReview"
    );
    const result = await fn(payload);
    if (!result.data.ok) throw new Error("Function returned not-ok");
    return { ok: true, data: undefined };
  } catch (err) {
    console.error("[submitReview]", err);
    return { ok: false, code: "review-failed", message: "Failed to submit review. Please try again." };
  }
}

// ─── FETCH REVIEWS ────────────────────────────────────────────────────────────
export async function fetchBookReviews(
  bookId:    string,
  maxCount = 10
): Promise<ApiResult<Review[]>> {
  try {
    const q = query(
      collection(db, "reviews"),
      where("bookId", "==", bookId),
      orderBy("createdAt", "desc"),
      limit(maxCount)
    );
    const snap = await getDocs(q);
    const reviews = snap.docs.map(d => ({
      id:           d.id,
      bookId:       d.data()["bookId"],
      requestId:    d.data()["requestId"],
      reviewerId:   d.data()["reviewerId"],
      reviewerName: d.data()["reviewerName"],
      rating:       d.data()["rating"],
      text:         d.data()["text"] ?? "",
      createdAt:    d.data()["createdAt"]?.toDate() ?? new Date(),
    }) as Review);
    return { ok: true, data: reviews };
  } catch (err) {
    console.error("[fetchBookReviews]", err);
    return { ok: false, code: "fetch-failed", message: "Failed to load reviews." };
  }
}
